import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { PAGE_TYPES } from '@dashboard/enums/page-types';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { formatPrice } from '@dashboard/services/formatting';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import { fetchInfinite } from '@dashboard/services/request';
import getChargeStatusLabel from '@dashboard/services/status/getChargeStatusLabel';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Button from '@dashboard/components/Button';
import { Checkbox, FormElement, Label, Select } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Lozenge from '@dashboard/components/Lozenge';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';

import Table from '../components/Table';

const Payments = () => {
  const { token } = useAuth();
  const history = useHistory();

  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [livemode, setLivemode] = useState(true);
  const [status, setStatus] = useState(query.status || 'all');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        livemode,
        status,
      }),
    });
  }, [sortBy, sortDirection, livemode, status]);

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['payments', sortBy, sortDirection, livemode, status],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/payments?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          ...(livemode !== undefined ? { livemode } : {}),
          ...(status !== 'all' ? { status } : {}),
          limit: 20,
          skip: pageParam * 20,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <div className="pr-4">
      <div className="flex items-end justify-end gap-x-2">
        <FormElement className="!mb-0">
          <div className="flex items-center gap-x-2 w-full">
            <Checkbox
              id="livemode"
              value={!livemode}
              checked={!livemode}
              onChange={() => setLivemode(livemode ? undefined : true)}
            />

            <Label isInline htmlFor="livemode">
              Include test payments
            </Label>
          </div>
        </FormElement>

        <FormElement className="!mb-0">
          <Label>Status</Label>
          <Select value={status} onChange={e => setStatus(e.target.value)} className="w-32">
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="refunded">Refunded</option>
            <option value="partially_paid">Partially paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="failed">Failed</option>
            <option value="created">Pending</option>
            <option value="abandoned">Abandoned</option>
          </Select>
        </FormElement>
      </div>

      <Table.Table>
        <Table.Head>
          <Table.Th
            onSort={onSort}
            sortKey="page.title"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Checkout
          </Table.Th>

          <Table.Th>Type</Table.Th>

          <Table.Th onSort={onSort} sortKey="amount" sortBy={sortBy} sortDirection={sortDirection}>
            Amount
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="seller.displayName"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Merchant
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Date
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="status" sortBy={sortBy} sortDirection={sortDirection}>
            Status
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(payment => (
                <Table.Row key={payment.id}>
                  <Table.Td>
                    <Link href={`/pages/${payment.page._id}/customize`}>
                      {payment.page.type === PAGE_TYPES.CHECKOUT && payment.product.title}
                      {payment.page.type === PAGE_TYPES.EVENT && payment.page.title}
                    </Link>

                    {!payment.livemode && (
                      <Tooltip label={payment.paymentError} side="right">
                        <StatusBadge status="test" withInfoIcon={!!payment.paymentError}>
                          Test
                        </StatusBadge>
                      </Tooltip>
                    )}
                  </Table.Td>

                  <Table.Td>{payment.page.type}</Table.Td>

                  <Table.Td>
                    {formatPrice({
                      amount: payment.amount,
                      currency: payment.currency,
                    })}
                  </Table.Td>
                  <Table.Td className="relative">
                    <Link href={`/admin/merchants/${payment.seller?._id}`}>
                      {payment.seller?.displayName || payment.seller?.name}
                    </Link>

                    {payment.seller?.disabled && (
                      <Lozenge className="ml-2 absolute right-0 mt-0.5">Disabled</Lozenge>
                    )}
                  </Table.Td>
                  <Table.Td>{format(new Date(payment.createdAt), 'd MMM, yyyy, HH:mm')}</Table.Td>
                  <Table.Td className="space-x-2">
                    <Tooltip label={payment.paymentError} side="right">
                      <StatusBadge
                        status={payment.status}
                        label={getChargeStatusLabel(payment.status)}
                        withInfoIcon={!!payment.paymentError}
                      />
                    </Tooltip>

                    <Button
                      to={`${getCheckoutUrl(payment.page, payment.seller)}`}
                      appearance="light"
                      target="_blank"
                      size="small"
                    >
                      View checkout
                    </Button>
                  </Table.Td>
                </Table.Row>
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </div>
  );
};

export default Payments;
